/* eslint-disable react/react-in-jsx-scope */
/*
 * File: CreateSalesOrder.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 13 May 2021 10:36:34 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Col, Divider, Modal, notification, Row, Space, Spin, Typography } from 'antd';
import { isFetchBaseQueryErrorType } from 'common/helpers/isFetchBaseQueryErrorType';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { SalesOrderFormNavigation } from 'components/molecules/SalesOrderFormNavigation';
import { FormikProvider, useFormik } from 'formik';
import { SalesOrderModel } from 'models/SalesOrderModel';
import { SalesOrderPayloadModel, salesOrderPayloadSchema } from 'models/SalesOrderPayloadModel';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { setDraftId } from 'redux/salesOrder/salesOrderDraftIdSlice';
import {
  useCreateAttachmentMutation,
  useCreateSalesOrderMutation,
  useGetSalesOrderQuery,
  useLazyGetSalesOrderQuery,
  usePostAttachmentMutation,
  usePostSalesOrderMutation
} from 'redux/services/useMamaBearService';
import '../../common/styles/waveAnimation.css';

interface CustomError {
  status: number;
  data: {
    errorCode: number;
    errorMessage: string;
  };
}

export const SalesOrderForm = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedCompanyDataAreaId, submitLoadingMessage } = useSelector((state: ReduxState) => state.salesOrders);


  const { id: salesOrderId } = useParams();
  /* ****************** Hook Api ****************** */
  const { data: salesOrderData, isLoading: isOrderLoading, isFetching } = useGetSalesOrderQuery({ id: salesOrderId as string }, { skip: !salesOrderId });

  const [postSalesOrder, { status, isError, isLoading, isSuccess, isUninitialized, error }] = usePostSalesOrderMutation({ fixedCacheKey: salesOrderId || 'newOrder' });
  const [createSalesOrder, { isLoading: isSaving }] = useCreateSalesOrderMutation();
  const [getSalesOrder, { isLoading: isGetting }] = useLazyGetSalesOrderQuery();

  const [createAttachment, { isLoading: isCreatingAttachment }] = useCreateAttachmentMutation();
  const [postAttachment, { isLoading: isPostingAttachment }] = usePostAttachmentMutation();

  const isFormLoading = Boolean(isLoading || isSaving || isGetting || isCreatingAttachment || isPostingAttachment || isFetching)


  /* ****************** Functions ****************** */


  const modalSuccessfulPost = useCallback(
    async (data: SalesOrderModel) => {
      const salesOrder = await getSalesOrder({ id: data.id as string });
      const { attachments } = salesOrder.data as SalesOrderModel;

      for (const attachment of attachments ?? []) {
        await postAttachment({ fileName: attachment.name, salesOrderId: data.id });
      }


      Modal.success({
        width: 1000,
        icon: <></>,
        onOk: (): void => navigate('/'),
        okButtonProps: { style: { position: 'absolute', bottom: 10, right: 10 } },
        className: 'confirmationModal',
        wrapClassName: 'confirmationModal',
        bodyStyle: { background: 'linear-gradient(to right bottom, #979797, #acacac, #c1c1c1, #d7d7d7, #ededed)', padding: 0 },
        content: (
          <>
            <Row>
              <Typography.Title level={3} style={{ margin: 20 }}>
                {`Sales Order Successfully Posted - ${salesOrder.data?.salesOrderNumber}`}
              </Typography.Title>
            </Row>
            <Divider style={{ marginTop: 0, marginBottom: 18 }} />

            <Row style={{ marginLeft: 20 }}>
              <Col span={6}>
                <Typography.Text>Product Name</Typography.Text>
              </Col>
              <Col span={2}>
                <Typography.Text>Status</Typography.Text>
              </Col>
              <Col span={16}>
                <Typography.Text>Message</Typography.Text>
              </Col>
            </Row>

            <Row style={{ maxHeight: 400, overflowY: 'auto', overflowX: 'hidden' }}>
              {(data as unknown as { updatedLines: any[] }).updatedLines.map((updatedLine) => (
                <Row
                  key={updatedLine}
                  justify="space-between"
                  align="middle"
                  style={{
                    // borderTop: index > 0 ? '1px solid rgba(0,0,0,.1)' : 'none',
                    background: '#ffffff70',
                    padding: '5px 25px',
                    borderRadius: 10,
                    margin: '10px 10px 0px 10px',
                    width: '100%',
                    minHeight: 55
                  }}>
                  <Col span={6}>{updatedLine.productNumber}</Col>
                  <Col span={2} style={{ justifyContent: 'center' }}>
                    {updatedLine.data?.status !== 400 ? (
                      <div style={{ width: 20, marginLeft: 10, marginTop: 5 }}>
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
                          <circle className="path circle" fill="none" stroke="#73AF55" strokeWidth="10" strokeMiterlimit="10" cx="65.1" cy="65.1" r="62.1" />
                          <polyline className="path check" fill="none" stroke="#73AF55" strokeWidth="10" strokeLinecap="round" strokeMiterlimit="10" points="100.2,40.2 51.5,88.8 29.8,67.5 " />
                        </svg>
                      </div>
                    ) : (
                      <div style={{ width: 20, marginLeft: 10, marginTop: 5 }}>
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
                          <circle className="path circle" fill="none" stroke="#D06079" strokeWidth="7" strokeMiterlimit="10" cx="65.1" cy="65.1" r="62.1" />
                          <line className="path line" fill="none" stroke="#D06079" strokeWidth="7" strokeLinecap="round" strokeMiterlimit="10" x1="34.4" y1="37.9" x2="95.8" y2="92.3" />
                          <line className="path line" fill="none" stroke="#D06079" strokeWidth="7" strokeLinecap="round" strokeMiterlimit="10" x1="95.8" y1="38" x2="34.4" y2="92.2" />
                        </svg>
                      </div>
                    )}
                  </Col>
                  <Col span={16}>{updatedLine.data.data?.errorMessage ?? 'Successfully posted'}</Col>
                </Row>
              ))}
            </Row>

            <Divider style={{ margin: '18px 0' }} />
            {(data as any).poResponse && <Row>
              <Typography.Title level={5} style={{ margin: 20 }}>
                {(data as any).poResponse}
              </Typography.Title>
            </Row>}
            <Col style={{ bottom: 0, width: '100%', marginTop: 50 }}>
              <svg className="waves" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 24 150 28" preserveAspectRatio="none" shapeRendering="auto">
                <defs>
                  <path id="gentle-wave" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
                </defs>
                <g className="parallax">
                  <use xlinkHref="#gentle-wave" x="48" y="0" fill="rgba(255,255,255,0.7" />
                  <use xlinkHref="#gentle-wave" x="48" y="3" fill="rgba(255,255,255,0.5)" />
                  <use xlinkHref="#gentle-wave" x="48" y="5" fill="rgba(255,255,255,0.3)" />
                  <use xlinkHref="#gentle-wave" x="48" y="7" fill="#fff" />
                </g>
              </svg>
            </Col>
          </>
        )
      });
    },
    [navigate]
  );


  /* ******************** Hooks ******************** */

  const formik = useFormik<SalesOrderPayloadModel>({
    validationSchema: salesOrderPayloadSchema,
    initialValues: {
      attention: salesOrderData && salesOrderId ? salesOrderData.attention : '',
      inventoryReservationMethod: salesOrderData && salesOrderId ? salesOrderData.inventoryReservationMethod : '',
      attachments: [],
      buyerId: salesOrderData && salesOrderId ? salesOrderData.buyerId || '' : '',
      buyerName: salesOrderData && salesOrderId ? salesOrderData.buyerName : '',
      convertedFromQuote: salesOrderData && salesOrderId ? salesOrderData.convertedFromQuote : false,
      currencyCode: 'usd',
      customerId: salesOrderData && salesOrderId ? salesOrderData.customerId : '',
      customerName: salesOrderData && salesOrderId ? salesOrderData.customerName : '',
      customerPo: salesOrderData && salesOrderId ? salesOrderData.customerPo : '',
      dataAreaId: selectedCompanyDataAreaId || '',
      deliveryAddressName: salesOrderData && salesOrderId ? salesOrderData?.deliveryAddressName || '' : '',
      deliveryAddressCity: salesOrderData && salesOrderId ? salesOrderData.deliveryAddressCity || '' : '',
      deliveryAddressCountry: salesOrderData && salesOrderId ? salesOrderData.deliveryAddressCountry || '' : '',
      deliveryAddressCounty: salesOrderData && salesOrderId ? salesOrderData.deliveryAddressCounty || '' : '',
      deliveryAddressDescription: salesOrderData && salesOrderId ? salesOrderData.deliveryAddressDescription || 'n/a' : undefined,
      deliveryAddressFormatted: salesOrderData && salesOrderId ? salesOrderData.deliveryAddressFormatted || '' : '',
      deliveryAddressLocationId: salesOrderData && salesOrderId ? salesOrderData.deliveryAddressLocationId || '' : '',
      deliveryAddressState: salesOrderData && salesOrderId ? salesOrderData.deliveryAddressState || '' : '',
      deliveryAddressStreet: salesOrderData && salesOrderId ? salesOrderData.deliveryAddressStreet || '' : '',
      deliveryAddressZipCode: salesOrderData && salesOrderId ? salesOrderData.deliveryAddressZipCode || '' : '',
      deliveryContactName: salesOrderData && salesOrderId ? salesOrderData.deliveryContactName || '' : '',
      deliveryContactPhone: salesOrderData && salesOrderId ? salesOrderData.deliveryContactPhone || '' : '',
      deliveryDate: salesOrderData && salesOrderId ? salesOrderData.requestedShipDate || '' : '',
      freightTerms: salesOrderData && salesOrderId ? salesOrderData.freightTerms || '' : '',
      isActive: salesOrderData && salesOrderId ? salesOrderData.isActive : false,
      labConfiguration: salesOrderData && salesOrderId ? salesOrderData.labConfiguration : false,
      languageId: 'en-us',
      lines: salesOrderData && salesOrderId ? salesOrderData.lines.map((line, idx) => ({ ...line, deliveryType: 'stock', lineNumber: idx + 1, subLines: line.subLines.map((subLine, childidx) => ({...subLine, lineNumber: (idx + 1)  + ((childidx + 1)* 0.01) }))  })) : [],
      modeOfDelivery: salesOrderData && salesOrderId ? salesOrderData.modeOfDelivery : '',
      paymentTerms: salesOrderData && salesOrderId ? salesOrderData.paymentTerms || '' : '',
      quoteId: salesOrderData && salesOrderId ? salesOrderData.quoteId || '' : '',
      requestedShipDate: salesOrderData && salesOrderId ? salesOrderData.requestedShipDate : '',
      salesOrderType: salesOrderData && salesOrderId ? salesOrderData.salesOrderType : 'SO',
      salesPersonFullName: salesOrderData && salesOrderId ? salesOrderData.salesPersonFullName || '' : '',
      salesPersonPersonnelNumber: salesOrderData && salesOrderId ? salesOrderData.salesPersonPersonnelNumber || '' : '',
      salesSupportFullName: salesOrderData && salesOrderId ? salesOrderData.salesSupportFullName || '' : '',
      salesSupportPersonnelNumber: salesOrderData && salesOrderId ? salesOrderData.salesSupportPersonnelNumber || '' : '',
      salesTaxGroupCode: 'avatax',
      shipComplete: salesOrderData && salesOrderId ? salesOrderData.shipComplete : false,
      siteId: salesOrderData && salesOrderId ? salesOrderData.siteId : '',
      status: salesOrderData && salesOrderId ? salesOrderData.status : '',
      warehouseId: salesOrderData && salesOrderId ? salesOrderData.warehouseId : ''
    },
    onSubmit: async (values) => {
      try {
        let saveResponse;

        if (!salesOrderId) {
          saveResponse = await createSalesOrder({ payload: values }).unwrap();
        }
        const attachmentsToPost = [];

        if (saveResponse && (saveResponse as unknown as { error: any }).error) return;

        const formData = new FormData();

        if (values.attachments) {
          for (const attachment of values.attachments) {
            formData.append(attachment.name, attachment);

            const attachmentResponse = await createAttachment({ file: formData, salesOrderId: salesOrderId ? salesOrderId : saveResponse?.id ? saveResponse?.id : '' });

            if (!(attachmentResponse as unknown as { error: any }).error) attachmentsToPost.push(attachment.name);
            formData.delete(attachment.name);
          }
        }
        const data = await postSalesOrder({ payload: values, payloadSalesOrderId: salesOrderId ? salesOrderId : saveResponse?.id ? saveResponse?.id : '' }).unwrap();



        modalSuccessfulPost(data);
      } catch (error) {
        console.log(error, 'error');
        if (isFetchBaseQueryErrorType(error)) {
          notification.error({
            message: 'Error',
            description: `${(error as CustomError).data.errorMessage}`,
            className: 'custom-class',
            style: {
              width: 600
            },
            duration: 10
          });
        }
      }
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false
  });


  useEffect(() => {
    if (salesOrderData) {
      const { draftId } = salesOrderData;

      if (draftId) dispatch(setDraftId(draftId));
    }
  }, [salesOrderData]);

  /* ****************** Render ****************** */
  return (
    <Spin
      spinning={isFormLoading}
      indicator={
        <>
          <BlockLoader direction="loader loader--slideUp" message={submitLoadingMessage} />
        </>
      }
      size="large">
      <FormikProvider value={formik}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <SalesOrderFormNavigation />
          <Outlet />
        </Space>
      </FormikProvider>
    </Spin>
  );
};
